/**
 * HUD du jalon de Phase 0 : taille de grille, fps, zoom, et les commandes qui permettent
 * de vérifier sur un vrai téléphone que la grille est bien **extensible** (§17, étape 3)
 * et que le rendu tient la cadence.
 *
 * Ce panneau est un outil de validation, pas l'interface finale : la boutique et les
 * compteurs de ressources arrivent en Phase 1.
 */

import { n, onLocaleChange, t } from '@/i18n';
import type { RendererStatus } from '@/render/cityRenderer';

export interface GridHudActions {
  readonly onExpand: () => void;
  readonly onShrink: () => void;
  readonly onRecenter: () => void;
}

export interface GridHud {
  update(status: RendererStatus): void;
  destroy(): void;
}

/** Le HUD se rafraîchit 5 fois par seconde : suffisant pour lire des fps, invisible en coût. */
const REFRESH_INTERVAL_MS = 200;

function requireElement(selector: string): HTMLElement {
  const element = document.querySelector<HTMLElement>(selector);
  if (element === null) {
    throw new Error(`Élément de HUD introuvable : ${selector}`);
  }
  return element;
}

export function createGridHud(actions: GridHudActions): GridHud {
  const panel = requireElement('#grid-hud');
  const gridLine = requireElement('#hud-grid');
  const perfLine = requireElement('#hud-perf');

  const bindings: [string, () => void][] = [
    ['#hud-expand', actions.onExpand],
    ['#hud-shrink', actions.onShrink],
    ['#hud-recenter', actions.onRecenter],
  ];

  for (const [selector, handler] of bindings) {
    requireElement(selector).addEventListener('click', handler);
  }

  let lastStatus: RendererStatus | null = null;
  let lastPaint = 0;

  const paint = (status: RendererStatus): void => {
    gridLine.textContent = t('hud.grid', { cols: status.grid.cols, rows: status.grid.rows });
    perfLine.textContent = t('hud.perf', {
      fps: Math.round(status.fps),
      zoom: n(status.zoom, { smallDecimals: 3 }),
      quality: status.quality,
    });
  };

  // Un changement de langue doit se voir immédiatement, sans attendre la frame suivante.
  const unsubscribe = onLocaleChange(() => {
    if (lastStatus !== null) {
      paint(lastStatus);
    }
  });

  panel.hidden = false;

  return {
    update(status: RendererStatus): void {
      lastStatus = status;
      const now = performance.now();
      if (now - lastPaint < REFRESH_INTERVAL_MS) {
        return;
      }
      lastPaint = now;
      paint(status);
    },
    destroy(): void {
      unsubscribe();
      panel.hidden = true;
    },
  };
}
